import type { BuyDeal, Lead, SaleListingStatus } from "@shared/schema";
import type { AdminSaleListing, BuyDealFormState, SaleListingFormState } from "./types";

export function userLabel(p: { email: string | null; displayName: string | null; userId: number | null }): string {
  return p.email || p.displayName || (p.userId == null ? "Anonymous" : `User #${p.userId}`);
}


export function formatNum(n: number): string {
  return new Intl.NumberFormat("en-US").format(Math.round(n));
}


export function formatUsd(n: number): string {
  if (n < 0.01 && n > 0) return `$${n.toFixed(4)}`;
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
}


export function csvEscape(value: unknown): string {
  if (value === null || value === undefined) return "";
  const str = String(value);
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}


// Neutralize spreadsheet formula injection for user-controlled text: a value
// that begins with =, +, -, or @ is treated as a formula by Excel/Sheets, so
// prefix it with a single quote to force literal text on open.
export function csvSafeText(value: unknown): string {
  if (value === null || value === undefined) return "";
  const str = String(value);
  return /^[=+\-@]/.test(str) ? `'${str}` : str;
}


export function rowsToCsv(rows: Array<Array<unknown>>): string {
  return rows.map((r) => r.map(csvEscape).join(",")).join("\r\n");
}


export function rangeSlug(hours: number): string {
  if (hours === 1) return "last-1-hour";
  if (hours === 24) return "last-24-hours";
  if (hours % 24 === 0) return `last-${hours / 24}-days`;
  return `last-${hours}-hours`;
}


export function downloadCsv(filename: string, content: string) {
  const blob = new Blob(["\ufeff" + content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}


export function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}


// Friendly label for the `source` column on app_user_subscription_status_events,
// surfaced inline next to a user's subscription status so admins can see at a
// glance how a comp/cancel/reactivate was applied (mirrors the Founder Signups
// "Manual · …" attribution).
export function subStatusSourceLabel(source: string): string {
  const map: Record<string, string> = {
    admin_override: "Manual",
    manual: "Manual",
    grant_founder: "Founder grant",
    founder_intent_activation: "Founder activation",
    padsplit: "PadSplit sync",
    stripe: "Stripe",
  };
  return map[source] || source;
}


export function planLabel(plan: string) {
  const map: Record<string, string> = {
    free: "Host",
    starter: "Host",
    research: "Research",
    Research: "Research",
    Host: "Host",
    growth: "Growth",
    Growth: "Growth",
    professional: "Professional",
    Professional: "Professional",
    enterprise: "Property Manager",
    Enterprise: "Property Manager",
    pmpro: "PM Pro",
    PMPro: "PM Pro",
    Interest: "Interest",
  };
  return map[plan] || plan;
}


export function planVariant(plan: string) {
  const normalized = plan.toLowerCase();
  if (normalized === "professional" || normalized === "enterprise") return "default" as const;
  if (normalized === "growth") return "default" as const;
  return "secondary" as const;
}


export function escapeCSV(value: string | number): string {
  const str = String(value);
  if (str.includes(",") || str.includes('"') || str.includes("\n")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}


export function formatStage(stage: string): string {
  return stage.replace(/_/g, " ");
}


export function downloadCSV(data: Lead[]) {
  const headers = [
    "Full Name",
    "Email",
    "Company",
    "Co-Living Stage",
    "Plan",
    "How They Heard About Us",
    "Interested in Mgmt ($350/mo)",
    "Date",
  ];
  const rows = data.map((l) => [
    escapeCSV(l.fullName),
    escapeCSV(l.email),
    escapeCSV(l.company || ""),
    escapeCSV(formatStage(l.journeyStage || "")),
    escapeCSV(planLabel(l.selectedPlan)),
    escapeCSV(l.howDidYouHearAboutUs || ""),
    escapeCSV(l.interestedInManagement === true ? "Yes" : l.interestedInManagement === false ? "No" : ""),
    new Date(l.createdAt).toISOString(),
  ]);
  const csv = [headers.map(escapeCSV), ...rows].map((r) => r.join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `padpulse-leads-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}


export function formatAgeMs(ageMs: number | null): string {
  if (ageMs == null) return "—";
  const seconds = Math.max(0, Math.floor(ageMs / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}


export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}


export function formatDigestSendHour(hour: number): string {
  if (!Number.isFinite(hour)) return "—";
  const h = ((hour % 24) + 24) % 24;
  const period = h < 12 ? "AM" : "PM";
  const display = h % 12 === 0 ? 12 : h % 12;
  return `${display}:00 ${period}`;
}


export function formatRelativeTime(iso: string | null | undefined): string {
  if (!iso) return "never";
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return "unknown";
  const diffMs = Date.now() - ts;
  if (diffMs < 0) return "in the future";
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}


export function toLocalInputValue(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}


export function formatFoundingAccessHistoryDate(iso: string | null): string {
  if (!iso) return "Env-var / compiled default";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}


export function parseEmailList(raw: string): string[] {
  return Array.from(
    new Set(
      raw
        .split(/[,\n;\s]+/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0),
    ),
  );
}


export function dealToForm(deal: BuyDeal): BuyDealFormState {
  const s = (v: unknown) => (v == null ? "" : String(v));
  return {
    address: deal.address,
    city: deal.city,
    state: deal.state,
    zipCode: s(deal.zipCode),
    market: deal.market,
    imageUrl: s(deal.imageUrl),
    photos: (deal.photos ?? []).filter(Boolean).join("\n"),
    purchasePrice: s(deal.purchasePrice),
    cashOnCash: s(deal.cashOnCash),
    monthlyCashFlow: s(deal.monthlyCashFlow),
    capRate: s(deal.capRate),
    currentBedrooms: s(deal.currentBedrooms),
    currentBathrooms: s(deal.currentBathrooms),
    futureBedrooms: s(deal.futureBedrooms),
    futureBathrooms: s(deal.futureBathrooms),
    sqft: s(deal.sqft),
    yearBuilt: s(deal.yearBuilt),
    propertyType: s(deal.propertyType),
    arv: s(deal.arv),
    rehabCost: s(deal.rehabCost),
    monthlyRent: s(deal.monthlyRent),
    monthlyExpenses: s(deal.monthlyExpenses),
    status: deal.status || "preview",
    notes: s(deal.notes),
    listingUrl: s(deal.listingUrl),
    isSample: !!deal.isSample,
  };
}


export function formToPayload(form: BuyDealFormState): Record<string, unknown> {
  const num = (v: string) => {
    const t = v.trim();
    if (t === "") return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  };
  const intNum = (v: string) => {
    const n = num(v);
    return n == null ? null : Math.trunc(n);
  };
  const str = (v: string) => {
    const t = v.trim();
    return t === "" ? null : t;
  };
  const photoLines = form.photos
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  return {
    address: form.address.trim(),
    city: form.city.trim(),
    state: form.state.trim(),
    zipCode: str(form.zipCode),
    market: form.market.trim(),
    imageUrl: str(form.imageUrl),
    photos: photoLines.length > 0 ? photoLines : null,
    purchasePrice: form.purchasePrice.trim(),
    cashOnCash: form.cashOnCash.trim(),
    monthlyCashFlow: form.monthlyCashFlow.trim(),
    capRate: str(form.capRate),
    currentBedrooms: intNum(form.currentBedrooms) ?? 0,
    currentBathrooms: intNum(form.currentBathrooms) ?? 0,
    futureBedrooms: intNum(form.futureBedrooms),
    futureBathrooms: intNum(form.futureBathrooms),
    sqft: intNum(form.sqft),
    yearBuilt: intNum(form.yearBuilt),
    propertyType: str(form.propertyType),
    arv: str(form.arv),
    rehabCost: str(form.rehabCost),
    monthlyRent: str(form.monthlyRent),
    monthlyExpenses: str(form.monthlyExpenses),
    status: form.status || "preview",
    notes: str(form.notes),
    listingUrl: str(form.listingUrl),
    isSample: form.isSample,
  };
}


export function listingToForm(l: AdminSaleListing): SaleListingFormState {
  return {
    askingPrice: l.askingPrice ?? "",
    status: (l.status as SaleListingStatus) ?? "active",
    description: l.description ?? "",
    sellerName: l.sellerName ?? "",
    contactEmail: l.contactEmail ?? "",
    contactPhone: l.contactPhone ?? "",
    photos: (l.photos ?? []).join("\n"),
  };
}


export function impersonationDuration(startedAt: string, endedAt: string | null) {
  const start = new Date(startedAt).getTime();
  const end = endedAt ? new Date(endedAt).getTime() : Date.now();
  const ms = end - start;
  const mins = Math.floor(ms / 60000);
  const secs = Math.floor((ms % 60000) / 1000);
  if (mins > 0) return `${mins}m ${secs}s`;
  return `${secs}s`;
}


export function formatLogDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}


export function formatLogTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}


export function formatCentsAdmin(cents: number) {
  return `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
}
